import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxWidth: '100%',
  overflowX: 'auto',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  border: '1px solid #E1E8ED',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 800,
  borderCollapse: 'separate',
  borderSpacing: 0,
}));

const TableWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
}));

const TableTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#063142',
  marginBottom: '16px',
  textAlign: 'center',
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#E7EFF5',
  color: '#063142',
  fontWeight: 600,
  textAlign: 'center',
  padding: '8px 8px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

const SubHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#E7EFF5',
  color: '#063142',
  fontWeight: 500,
  textAlign: 'center',
  border: '1px solid #E1E8ED',
  padding: '6px 4px',
  fontSize: '12px',
  whiteSpace: 'pre-line',
  lineHeight: '1.2',
  verticalAlign: 'middle',
}));

const DataCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #E1E8ED',
  padding: '6px 6px',
  textAlign: 'right',
  fontSize: '13px',
  whiteSpace: 'nowrap',
  '&.total-row': {
    fontWeight: 600,
  },
}));

const QuarterCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #E1E8ED',
  padding: '6px 6px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: 500,
  color: '#063142',
  backgroundColor: '#FAFBFC',
  '&.total-row': {
    fontWeight: 600,
  },
}));

// Column structure for CN56 Non-sampled flows
const columnStructure = [
  { accessor: 'quarter', header: 'Quarter', width: 80 },
  { accessor: 'a_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'a_number_of_items', header: 'Number\nof items', width: 100 },
  { accessor: 'd_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'd_number_of_items', header: 'Number\nof items', width: 100 },
  { accessor: 'b_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'b_number_of_items', header: 'Number\nof items', width: 100 },
  { accessor: 'c_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'c_number_of_items', header: 'Number\nof items', width: 100 },
];

// Column groups for grouped headers
const columnGroups = [
  {
    header: 'Mail Category A',
    columns: ['a_actual_weight', 'a_number_of_items'],
  },
  {
    header: 'Mail Category D',
    columns: ['d_actual_weight', 'd_number_of_items'],
  },
  {
    header: 'Mail Category B',
    columns: ['b_actual_weight', 'b_number_of_items'],
  },
  {
    header: 'Mail Category C',
    columns: ['c_actual_weight', 'c_number_of_items'],
  },
];

// Calculate column widths
const calculateColumnWidths = (data: any[], columns: any[]) => {
  const newColumnWidths: Record<string, string> = {};
  const minColWidth = 80;
  const maxColWidth = 200;

  columns.forEach((column) => {
    let maxWidth = minColWidth;

    // Check header text width
    const headerWidth = column.header.length * 8;
    maxWidth = Math.max(maxWidth, headerWidth);

    // Check data content width
    data.forEach((row) => {
      const value = row[column.accessor];
      if (value !== null && value !== undefined) {
        const cellWidth = value.toString().length * 8;
        maxWidth = Math.max(maxWidth, cellWidth);
      }
    });

    newColumnWidths[column.accessor] = Math.min(
      Math.max(maxWidth, minColWidth),
      maxColWidth
    ).toString();
  });

  return newColumnWidths;
};

// Number formatting function - handles both formatted strings and raw numbers
const formatNumber = (value: number | string | null | undefined, columnAccessor: string): string => {
  if (value === null || value === undefined) return '';
  
  // If value is already a formatted string, return it as-is
  if (typeof value === 'string') {
    return value;
  }

  // Otherwise, format the number
  if (columnAccessor.includes('actual_weight')) {
    return value.toFixed(3);
  } else if (columnAccessor.includes('number_of_items')) {
    return Math.round(value).toString();
  }

  return value.toString();
};

// Render cell content
const renderCell = (row: any, column: any) => {
  const value = row[column.accessor];

  if (column.accessor === 'quarter') {
    return row[column.accessor];
  }

  return formatNumber(value, column.accessor);
};

// Check if column is numeric
const isNumericColumn = (accessor: string): boolean => {
  return accessor !== 'quarter';
};

export interface CN56NonSampledRow {
  id: string;
  quarter: string;
  // Can be formatted strings or numbers
  a_actual_weight: number | string | null | undefined;
  a_number_of_items: number | string | null | undefined;
  d_actual_weight: number | string | null | undefined;
  d_number_of_items: number | string | null | undefined;
  b_actual_weight: number | string | null | undefined;
  b_number_of_items: number | string | null | undefined;
  c_actual_weight: number | string | null | undefined;
  c_number_of_items: number | string | null | undefined;
}

export interface CN56NonSampledTableProps {
  data: CN56NonSampledRow[];
  moduleName?: string;
  viewMode?: boolean;
  disableAutoSizing?: boolean;
  pageSize?: number;
  lazyLoad?: boolean;
  lazyLoadHeight?: number;
  lazyLoadingBatchSize?: number;
  rowBackgroundColor?: string;
}

function CN56NonSampledTable({
  data,
  moduleName = 'CN56 Non-Sampled Flows',
  viewMode = false,
  disableAutoSizing = false,
  pageSize = 25,
  lazyLoad = false,
  lazyLoadHeight = 400,
  lazyLoadingBatchSize = 25,
  rowBackgroundColor,
}: CN56NonSampledTableProps) {
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [isResizing, setIsResizing] = useState(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  // Calculate column widths on data change
  useEffect(() => {
    if (!disableAutoSizing && data.length > 0) {
      const widths = calculateColumnWidths(data, columnStructure);
      setColumnWidths(widths);
    }
  }, [data, disableAutoSizing]);

  // Handle column resize
  const handleColumnResize = useCallback((columnAccessor: string, newWidth: number) => {
    setIsResizing(true);

    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      setColumnWidths((prev) => ({
        ...prev,
        [columnAccessor]: Math.max(80, Math.min(300, newWidth)).toString(),
      }));
      setIsResizing(false);
    }, 100);
  }, []);

  if (!data || data.length === 0) {
    return (
      <TableWrapper data-testid="cn56-non-sampled-table-wrapper-empty">
        <TableTitle data-testid="cn56-non-sampled-table-title-empty">No data available</TableTitle>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper data-testid="cn56-non-sampled-table-wrapper">
      <TableTitle data-testid="cn56-non-sampled-table-title">{moduleName}</TableTitle>

      <StyledTableContainer data-testid="cn56-non-sampled-table-container">
        <StyledPaper data-testid="cn56-non-sampled-table-paper">
          <StyledTable data-testid="cn56-non-sampled-table">
            <TableHead data-testid="cn56-non-sampled-table-head">
              {/* Main header row */}
              <TableRow data-testid="cn56-non-sampled-table-header-row">
                <HeaderCell style={{ width: columnWidths.quarter || '80px' }} data-testid="cn56-non-sampled-table-quarter-header">Quarter</HeaderCell>

                {columnGroups.map((group) => (
                  <HeaderCell
                    key={group.header}
                    colSpan={group.columns.length}
                    style={{ textAlign: 'center' }}
                    data-testid={`cn56-non-sampled-table-group-header-${group.header.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {group.header}
                  </HeaderCell>
                ))}
              </TableRow>

              {/* Sub-header row */}
              <TableRow data-testid="cn56-non-sampled-table-subheader-row">
                <SubHeaderCell data-testid="cn56-non-sampled-table-quarter-subheader"></SubHeaderCell>

                {columnGroups.map((group) =>
                  group.columns.map((columnAccessor) => {
                    const column = columnStructure.find((col) => col.accessor === columnAccessor);
                    return (
                      <SubHeaderCell
                        key={columnAccessor}
                        style={{ width: columnWidths[columnAccessor] || column?.width || '100px' }}
                        data-testid={`cn56-non-sampled-table-subheader-${columnAccessor}`}
                      >
                        {column?.header || columnAccessor}
                      </SubHeaderCell>
                    );
                  })
                )}
              </TableRow>
            </TableHead>

            <TableBody data-testid="cn56-non-sampled-table-body">
              {data.map((row: any, rowIndex: number) => (
                <TableRow
                  key={row.id || rowIndex}
                  data-testid={`cn56-non-sampled-table-row-${rowIndex}`}
                  sx={{
                    backgroundColor: rowBackgroundColor || 'inherit',
                    '&:hover': { backgroundColor: '#F5F5F5' },
                    // Add bold borders after Total rows
                    borderBottom: row.quarter === 'Total' ? '1px solid #063142' : '1px solid #E1E8ED',
                  }}
                >
                  {/* Quarter column */}
                  <QuarterCell 
                    className={row.quarter === 'Total' ? 'total-row' : ''}
                    style={{ width: columnWidths.quarter || '80px' }}
                    data-testid={`cn56-non-sampled-table-quarter-cell-${rowIndex}`}
                  >
                    {row.quarter}
                  </QuarterCell>

                  {/* Data columns */}
                  {columnGroups.map((group, groupIndex) =>
                    group.columns.map((columnAccessor, colIndex) => {
                      const column = columnStructure.find(
                        (col) => col.accessor === columnAccessor
                      );
                      return (
                        <DataCell
                          key={columnAccessor}
                          className={row.quarter === 'Total' ? 'total-row' : ''}
                          sx={{
                            width: columnWidths[columnAccessor] || column?.width || '100px',
                            textAlign: isNumericColumn(columnAccessor) ? 'right' : 'center',
                            // Add bold right border for the last column of each group
                            borderRight: colIndex === group.columns.length - 1 ? '1px solid #063142' : '1px solid #E1E8ED',
                          }}
                          data-testid={`cn56-non-sampled-table-data-cell-${columnAccessor}-${rowIndex}`}
                        >
                          {renderCell(
                            row,
                            column || { accessor: columnAccessor, header: columnAccessor }
                          )}
                        </DataCell>
                      );
                    })
                  )}
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </StyledPaper>
      </StyledTableContainer>
    </TableWrapper>
  );
}

export default CN56NonSampledTable;
